import { ReactNode, useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  InputField,
  cn,
} from "@jem-hr/jem-ui";
import { History, Building2 } from "lucide-react";
import { GeofenceMapPreview } from "./GeofenceMapPreview";

export interface SiteConfigCardProps {
  siteName: string;
  address: string;
  latitude: string;
  longitude: string;
  geofenceRadius: string;
  lateArrivalThreshold: string;
  forceEditing?: boolean;
  onChange: (field: SiteFieldKey, value: string) => void;
  onOpenHistory: () => void;
  onSave?: () => void;
  onDiscard?: () => void;
  children?: ReactNode;
  className?: string;
}

export type SiteFieldKey =
  | "siteName"
  | "address"
  | "latitude"
  | "longitude"
  | "geofenceRadius"
  | "lateArrivalThreshold";

export function SiteConfigCard({
  siteName,
  address,
  latitude,
  longitude,
  geofenceRadius,
  lateArrivalThreshold,
  forceEditing,
  onChange,
  onOpenHistory,
  onSave,
  onDiscard,
  children,
  className,
}: SiteConfigCardProps) {
  const [accordionValue, setAccordionValue] = useState<string>("site-card");

  useEffect(() => {
    if (forceEditing) setAccordionValue("site-card");
  }, [forceEditing]);

  return (
    <Accordion
      type="single"
      collapsible
      value={accordionValue}
      onValueChange={setAccordionValue}
      className={cn(
        "rounded-lg border border-greyscale-border bg-white",
        className
      )}
    >
      <AccordionItem value="site-card" className="border-0">
        <AccordionTrigger className="!px-10 py-md hover:no-underline">
          <div className="flex items-center gap-xs">
            <div className="rounded-md bg-secondary-pink-100 p-xxs">
              <Building2 size={20} className="text-secondary-pink-900" />
            </div>
            <div className="text-left">
              <h2 className="font-heading text-lg font-semibold text-greyscale-text-title">
                {siteName} settings
              </h2>
              <p className="text-xs text-greyscale-text-caption">
                <strong className="text-greyscale-text-title">
                  {geofenceRadius} m
                </strong>{" "}
                geofence
                <span className="mx-xxs">·</span>
                <strong className="text-greyscale-text-title">
                  {lateArrivalThreshold} min
                </strong>{" "}
                late arrival threshold
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="border-t border-greyscale-border">
            <div className="space-y-md p-md">
          <FieldCluster
            title="Site Details"
            hint="Identification for this Site."
            action={
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<History size={14} />}
                onClick={onOpenHistory}
              >
                View audit history
              </Button>
            }
          >
            <div className="sm:max-w-[50%]">
              <InputField
                label="Site name"
                required
                value={siteName}
                onChange={(e) => onChange("siteName", e.target.value)}
              />
            </div>
          </FieldCluster>

          <FieldCluster
            title="Default Location & Geofence"
            hint="Set the default GPS coordinates and geofence radius for shift locations under this Site."
          >
            <div className="grid gap-md md:grid-cols-2">
              <div className="space-y-md">
                <InputField
                  label="Address"
                  required
                  value={address}
                  onChange={(e) => onChange("address", e.target.value)}
                />
                <div className="grid gap-md sm:grid-cols-2">
                  <InputField
                    label="Latitude"
                    required
                    value={latitude}
                    onChange={(e) => onChange("latitude", e.target.value)}
                    helperText="Decimal degrees, e.g. -26.20227"
                  />
                  <InputField
                    label="Longitude"
                    required
                    value={longitude}
                    onChange={(e) => onChange("longitude", e.target.value)}
                    helperText="Decimal degrees, e.g. 28.04363"
                  />
                </div>
                <InputField
                  label="Geofence radius (metres)"
                  required
                  type="number"
                  value={geofenceRadius}
                  onChange={(e) => onChange("geofenceRadius", e.target.value)}
                />
              </div>
              <GeofenceMapPreview
                latitude={Number(latitude) || 0}
                longitude={Number(longitude) || 0}
                radiusMeters={Number(geofenceRadius) || 100}
                source="site"
                siteName={siteName}
              />
            </div>
          </FieldCluster>

          <FieldCluster
            title="Default Late Arrival Threshold"
            hint="Minutes before an employee is marked as a late arrival."
          >
            <div className="sm:max-w-[50%]">
              <InputField
                label="Threshold (minutes)"
                type="number"
                value={lateArrivalThreshold}
                onChange={(e) => onChange("lateArrivalThreshold", e.target.value)}
              />
            </div>
          </FieldCluster>

          <div className="flex items-center justify-end gap-xs">
            {forceEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onDiscard?.();
                  setAccordionValue("");
                }}
              >
                Discard
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAccordionValue("")}
              >
                Close
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              disabled={!forceEditing}
              onClick={() => {
                onSave?.();
                setAccordionValue("");
              }}
            >
              Save changes
            </Button>
          </div>
            </div>

            {children}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function FieldCluster({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="border-t border-greyscale-border pt-md first:border-t-0 first:pt-0">
      <div className="mb-sm flex items-start justify-between gap-sm">
        <div>
          <h3 className="text-sm font-semibold text-greyscale-text-title">
            {title}
          </h3>
          <p className="text-xs text-greyscale-text-caption">{hint}</p>
        </div>
        {action}
      </div>
      <div className="space-y-md">{children}</div>
    </div>
  );
}
